import { useState } from "react";

import type { UseFormReturn } from "react-hook-form";

import { Button, Icon, Text, Tooltip, toastError } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { postAgreementForLegalEntityAndSigner } from "~/v2/api/rest/entityOnboarding";
import { CUSTOMER_AGREEMENT_REVISION } from "../../../onboarding/constants";
import { CUSTOMER_AGREEMENT_URL, REQUIRED_ERR_MSG } from "../../constants";
import { Radio } from "../../Radio";
import { ViewAndAccept } from "../../ViewAndAccept";
import type { Agreements } from "./schema";

export interface AgreementsFormProps {
  accountId: string;
  legalEntityOwnerId: string;
  accountOpenerOwnerId: string;
  form: UseFormReturn<Agreements>;
  onBackClicked: () => void;
  onSubmitClicked: () => void;
  isSubmitDisabled: boolean;
}

export const AgreementsForm = ({
  form,
  accountId,
  legalEntityOwnerId,
  accountOpenerOwnerId,
  onBackClicked,
  onSubmitClicked,
  isSubmitDisabled,
}: AgreementsFormProps) => {
  const [isCustomerAgreementViewed, setIsCustomerAgreementViewed] =
    useState(false);

  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const isCertificationsAndResolutionsAccepted = watch(
    "certifications_and_resolutions_accepted",
  );
  const isCustomerAgreementAcknowledged = watch(
    "customer_agreement_acknowledged",
  );
  const isDigitalSignatureAcknowledged = watch(
    "digital_signature_acknowledged",
  );
  const isIDVerificationAcknowledged = watch("id_verification_acknowledged");
  const isUBOAcknowledged = watch("ubo_acknowledged");

  const isAgreementsRequiredError = [
    errors?.digital_signature_acknowledged,
    errors?.id_verification_acknowledged,
    errors?.ubo_acknowledged,
  ].some((error) => !!error);

  const customerAgreementMutation = useMutationRequest(
    () =>
      postAgreementForLegalEntityAndSigner({
        ownerId: legalEntityOwnerId,
        agreement: "customer_agreement",
        accountId,
        signerPartyId: accountOpenerOwnerId,
        revision: CUSTOMER_AGREEMENT_REVISION,
      }),
    {
      onSuccess: () => {
        setValue("customer_agreement_acknowledged", true);
      },
      onError: (err) => {
        const errorMsg = getErrorMessage(err);
        toastError(`Error acknowledging customer agreement, ${errorMsg}`);
        setValue("customer_agreement_acknowledged", false);
      },
    },
  );

  return (
    <div className="flex flex-col gap-5">
      <ViewAndAccept
        accountId={accountId}
        ownerId={legalEntityOwnerId}
        taxForm="certifications_and_resolutions_statement"
        isAccepted={isCertificationsAndResolutionsAccepted}
        onChange={(isAccepted) =>
          setValue("certifications_and_resolutions_accepted", isAccepted)
        }
        errorMessage={errors?.certifications_and_resolutions_accepted?.message}
        signerPartyId={accountOpenerOwnerId}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-lg">
            Alpaca Customer Agreement Acknowledgement
          </h2>
          {errors?.customer_agreement_acknowledged?.message && (
            <Text className="truncate text-rose-500 text-xs">
              {REQUIRED_ERR_MSG}
            </Text>
          )}
        </div>
        <Text className="text-sm">
          I have read, understood, and agree to be bound by Alpaca Securities
          LLC account terms, and all other terms, disclosures, and disclaimers
          applicable to me and the legal entity at hand, as referenced in the
          Alpaca Customer Agreement.
        </Text>
        <Text className="text-sm">
          I also acknowledge that the Alpaca Customer Agreement contains a
          pre-dispute arbitration clause in Section 43.
        </Text>
        <Button
          variant="alternate"
          type="button"
          onClick={() => {
            window.open(CUSTOMER_AGREEMENT_URL, "_blank", "noopener");
            setIsCustomerAgreementViewed(true);
          }}
          className="max-w-fit"
        >
          Alpaca Customer Agreement.pdf
          <Icon name="ArrowTopRightOnSquare" className="ml-2" />
        </Button>
        <Tooltip
          content={
            isCustomerAgreementViewed
              ? isCustomerAgreementAcknowledged
                ? "Accepted"
                : "Click to accept document"
              : "Must view document first."
          }
        >
          <Radio
            name="customer_agreement_acknowledged"
            label="I read and agree to the Alpaca Customer Agreement"
            isChecked={isCustomerAgreementAcknowledged}
            onChange={(isChecked) =>
              isChecked && customerAgreementMutation.mutate(true)
            }
            disabled={
              !isCustomerAgreementViewed || isCustomerAgreementAcknowledged
            }
          />
        </Tooltip>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="font-medium text-lg">
          Digital Signature Acknowledgement
        </h2>
        <Text className="text-sm">
          By clicking 'I Agree' I understand I am signing this agreement
          electronically, and that my electronic signature will have the same
          effect as physically signing and returning the Application Agreement.
        </Text>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="font-medium text-lg">
          Ultimate Beneficial Owners Acknowledgement
        </h2>
        <Text className="text-sm">
          I acknowledge that federal regulation requires Alpaca Securities LLC
          to obtain, verify, and record information about the beneficial owners
          of legal business customers. I confirm that the information I provided
          regarding the beneficial owners of the legal business for which I am
          opening this account is complete and correct and that there is no
          additional natural person who owns 10% or more of the legal entity
          other than those listed in this application.
        </Text>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="font-medium text-lg">
          Identity Verification Acknowledgement
        </h2>
        <Text className="text-sm">
          I understand that federal law requires that Alpaca Securities LLC
          verify my identity by obtaining my name, date of birth, address, and a
          valid government-issued identification number before opening the
          account.
        </Text>
        <Text className="text-sm">
          In certain circumstances, Alpaca Securities LLC may obtain and verify
          this information concerning any person(s) authorized to effect
          transactions in an account or that is considered an ultimate
          beneficial owner of the business. Identifying documentation for the
          business is also required.
        </Text>
        <Text className="text-sm">
          The account may be restricted and/or closed if Alpaca Securities LLC
          cannot verify this information. Alpaca Securities LLC will not be
          responsible for any losses or damages (including but not limited to
          lost opportunities) resulting from any failure to provide this
          information, or from any restriction placed upon, or closing of, the
          account.
        </Text>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-lg">Agreements</h2>
          {isAgreementsRequiredError && (
            <Text className="truncate text-rose-500 text-xs">
              {REQUIRED_ERR_MSG}
            </Text>
          )}
        </div>
        <Radio
          name="digital_signature_acknowledged"
          label="I read and agree to the Digital Signature Acknowledgment"
          isChecked={isDigitalSignatureAcknowledged}
          onChange={(isChecked) =>
            setValue("digital_signature_acknowledged", isChecked)
          }
        />
        <Radio
          name="ubo_acknowledged"
          label="I read and agree to the Ultimate Beneficial Owners Acknowledgement"
          isChecked={isUBOAcknowledged}
          onChange={(isChecked) => setValue("ubo_acknowledged", isChecked)}
        />
        <Radio
          name="id_verification_acknowledged"
          label="I read and agree to the Identity Verification Acknowledgement"
          isChecked={isIDVerificationAcknowledged}
          onChange={(isChecked) =>
            setValue("id_verification_acknowledged", isChecked)
          }
        />
      </div>

      <div className="flex w-full items-center justify-between gap-4 pt-9">
        <Button type="button" onClick={onBackClicked} variant="secondary">
          Back
        </Button>
        <Button
          type="button"
          onClick={onSubmitClicked}
          disabled={isSubmitDisabled}
        >
          I Agree
        </Button>
      </div>
    </div>
  );
};
